/**
 * React binding for the framework-agnostic session client, using `useSyncExternalStore` so subscribers only
 * re-render when the client's snapshot reference actually changes. The client is created once per mount and
 * closed on unmount; callers that need a different session should remount (e.g. key by sessionId).
 *
 * Effects can re-run without an unmount (React Fast Refresh in development, StrictMode double-invocation).
 * When the cleanup has disposed the client, the next effect run creates a fresh one instead of leaving a dead
 * client in place; a client closed by the server (superseded, removed, ended) is never recreated here.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createSessionClient, type SessionClient, type SessionClientOptions, type SessionClientSnapshot } from './client.ts';

export interface UseSessionClientResult {
  client: SessionClient;
  snapshot: SessionClientSnapshot;
}

export function useSessionClient(options: SessionClientOptions): UseSessionClientResult {
  const [client, setClient] = useState<SessionClient>(() => createSessionClient(options));
  const disposedByHook = useRef(false);

  useEffect(() => {
    if (disposedByHook.current) {
      disposedByHook.current = false;
      setClient(createSessionClient(options));
      return undefined;
    }
    const active = client;
    return () => {
      active.close();
      disposedByHook.current = true;
    };
    // options are captured at creation time; a different session must remount (key by sessionId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const snapshot = useSyncExternalStore(client.subscribe, client.getSnapshot, client.getSnapshot);

  return { client, snapshot };
}
